import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../styles/formularios.css';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';

import logoISC from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png';
import firmaCoordinadora from '../components/images/firma_coordinadora.png';
import { resolveApiBaseList } from '../utils/apiBase';

const CCT = '14PPR0155G';

const obtenerNumeroGrado = (datosAlumno) => {
  const gradoDirecto = datosAlumno?.alumno?.grado ?? datosAlumno?.alumno?.Grado;
  if (gradoDirecto !== undefined && gradoDirecto !== null) {
    const numero = Number(gradoDirecto);
    if (!Number.isNaN(numero)) {
      return numero;
    }
  }

  const grupoTexto = datosAlumno?.alumno?.grupo || datosAlumno?.alumno?.Grupo || '';
  const match = grupoTexto.match(/(\d+)/);
  if (match) {
    const numero = Number(match[1]);
    return Number.isNaN(numero) ? null : numero;
  }

  return null;
};

const obtenerFasePorGrado = (gradoNumero) => {
  if (!gradoNumero) {
    return null;
  }

  if ([1, 2].includes(gradoNumero)) {
    return 3;
  }

  if ([3, 4].includes(gradoNumero)) {
    return 4;
  }

  if ([5, 6].includes(gradoNumero)) {
    return 5;
  }

  return null;
};

const ConsultaCalificaciones = () => {
  const [grupos, setGrupos] = useState([]);
  const [alumnos, setAlumnos] = useState([]);
  const [grupoSeleccionado, setGrupoSeleccionado] = useState('');
  const [alumnoSeleccionado, setAlumnoSeleccionado] = useState('');
  const [datosAlumno, setDatosAlumno] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [loadingAlumnos, setLoadingAlumnos] = useState(false);
  const [error, setError] = useState('');
  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // mantener mensaje original
          }
          const errorItem = new Error(message);
          errorItem.status = response.status;
          lastError = errorItem;
          console.warn(`Warning: petición fallida a ${url}:`, errorItem);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (!trimmed) return {};

        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return JSON.parse(trimmed);
        }

        try {
          return JSON.parse(trimmed);
        } catch (parseErr) {
          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`Warning: contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
        }
      } catch (errorItem) {
        lastError = errorItem;
        console.warn(`Warning: error de red al solicitar ${url}:`, errorItem);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  const cargarGrupos = useCallback(async () => {
    try {
      setLoadingGrupos(true);
      setError('');

      const data = await fetchJsonWithFallback('/grupos/activos');
      setGrupos(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Error al cargar grupos activos:', err);
      setError(err.message || 'Ocurrió un error al cargar los grupos activos.');
    } finally {
      setLoadingGrupos(false);
    }
  }, [fetchJsonWithFallback]);

  const cargarAlumnos = useCallback(async (idGrupo) => {
    if (!idGrupo) {
      setAlumnos([]);
      return;
    }

    try {
      setLoadingAlumnos(true);
      setError('');

      const data = await fetchJsonWithFallback(`/grupos/${encodeURIComponent(idGrupo)}/alumnos-detalle`);
      setAlumnos(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Error al cargar alumnos del grupo:', err);
      setError(err.message || 'Ocurrió un error al cargar los alumnos del grupo.');
    } finally {
      setLoadingAlumnos(false);
    }
  }, [fetchJsonWithFallback]);

  const cargarCalificacionesAlumno = useCallback(async (idUsuario) => {
    if (!idUsuario) {
      setDatosAlumno(null);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const data = await fetchJsonWithFallback(`/calificaciones/alumno/${encodeURIComponent(idUsuario)}`);
      if (!data || typeof data !== 'object') {
        throw new Error('No fue posible obtener la boleta del alumno seleccionado.');
      }
      setDatosAlumno(data);
    } catch (err) {
      console.error('❌ Error al cargar calificaciones del alumno:', err);
      setDatosAlumno(null);
      if (err?.status === 404) {
        setError('No se encontraron calificaciones para el alumno seleccionado.');
      } else {
        setError(err.message || 'Ocurrió un error al cargar la boleta del alumno.');
      }
    } finally {
      setLoading(false);
    }
  }, [fetchJsonWithFallback]);

  useEffect(() => {
    cargarGrupos();
  }, [cargarGrupos]);

  useEffect(() => {
    if (!grupoSeleccionado) {
      setAlumnos([]);
      return;
    }
    cargarAlumnos(grupoSeleccionado);
  }, [grupoSeleccionado, cargarAlumnos]);

  useEffect(() => {
    if (!alumnoSeleccionado) {
      setDatosAlumno(null);
      return;
    }
    cargarCalificacionesAlumno(alumnoSeleccionado);
  }, [alumnoSeleccionado, cargarCalificacionesAlumno]);

  const handleGrupoChange = useCallback((event) => {
    const value = event.target.value;
    setGrupoSeleccionado(value);
    setAlumnoSeleccionado('');
    setDatosAlumno(null);
    setAlumnos([]);
    setLoading(false);
    setError('');
  }, []);

  const handleAlumnoChange = useCallback((event) => {
    const value = event.target.value;
    setAlumnoSeleccionado(value);
    setDatosAlumno(null);
    setLoading(false);
    setError('');
  }, []);

  const categorias = useMemo(() => Object.values(datosAlumno?.categorias || {}), [datosAlumno]);

  const trimestres = useMemo(
    () => Object.keys(datosAlumno?.trimestres || {}).sort((a, b) => Number(a) - Number(b)),
    [datosAlumno]
  );

  const totalColumnasMaterias = useMemo(
    () => categorias.reduce((total, categoria) => total + Object.keys(categoria.materias || {}).length, 0),
    [categorias]
  );

  const fase = useMemo(() => {
    const grado = obtenerNumeroGrado(datosAlumno);
    return obtenerFasePorGrado(grado);
  }, [datosAlumno]);

  const promediosCategorias = useMemo(() => (
    categorias.map((categoria) => {
      let suma = 0;
      let contador = 0;

      Object.values(categoria.materias || {}).forEach((materia) => {
        Object.values(materia.calificaciones || {}).forEach((registro) => {
          const valor = Number.parseFloat(registro?.calificacion);
          if (!Number.isNaN(valor)) {
            suma += valor;
            contador += 1;
          }
        });
      });

      return contador > 0 ? suma / contador : null;
    })
  ), [categorias]);

  const promediosCategoriaPorTrimestre = useMemo(() => {
    const resultado = {};

    trimestres.forEach((trimestre) => {
      resultado[trimestre] = categorias.map((categoria) => {
        let suma = 0;
        let contador = 0;

        Object.values(categoria.materias || {}).forEach((materia) => {
          const valor = Number.parseFloat(materia.calificaciones?.[trimestre]?.calificacion);
          if (!Number.isNaN(valor)) {
            suma += valor;
            contador += 1;
          }
        });

        return contador > 0 ? suma / contador : null;
      });
    });

    return resultado;
  }, [categorias, trimestres]);

  const handleExportarExcel = useCallback(() => {
    if (!datosAlumno) {
      return;
    }

    try {
      const wb = XLSX.utils.book_new();
      const datosExcel = [];

      datosExcel.push(['INSTITUTO SILVIANO CARRILLO']);
      datosExcel.push(['Boleta de Evaluaciones Sección PRIMARIA']);
      datosExcel.push([]);

      if (datosAlumno?.alumno) {
        datosExcel.push(['Información del Alumno']);
        datosExcel.push(['Nombre:', datosAlumno.alumno.nombre]);
        datosExcel.push(['Grupo:', datosAlumno.alumno.grupo]);
        datosExcel.push(['Ciclo Escolar:', datosAlumno.alumno.cicloEscolar || '2024-2025']);
        datosExcel.push(['C.C.T.:', `C.C.T. ${CCT}`]);
        datosExcel.push(['Fase:', fase ? `Fase ${fase}` : '—']);
        datosExcel.push(['Fecha:', new Date().toLocaleDateString('es-ES')]);
        datosExcel.push([]);
      }

      const encabezados = ['PERIODO'];
      const todasLasMaterias = [];

      categorias.forEach((categoria) => {
        Object.keys(categoria.materias || {}).forEach((nombreMateria) => {
          encabezados.push(nombreMateria.toUpperCase());
          todasLasMaterias.push(nombreMateria);
        });
      });
      encabezados.push('PROMEDIO');

      datosExcel.push(encabezados);

      trimestres.forEach((trimestre) => {
        const fila = [`${trimestre}°`];

        todasLasMaterias.forEach((nombreMateria) => {
          let calificacion = '-';

          for (const categoria of categorias) {
            if (categoria.materias?.[nombreMateria]?.calificaciones?.[trimestre]) {
              calificacion = categoria.materias?.[nombreMateria]?.calificaciones?.[trimestre]?.calificacion ?? '-';
              break;
            }
          }

          fila.push(calificacion);
        });

        fila.push(datosAlumno?.promedios?.porTrimestre?.[trimestre] || '-');
        datosExcel.push(fila);
      });

      datosExcel.push([]);
      datosExcel.push(['PROMEDIO GENERAL:', datosAlumno?.promedios?.general || '0.0']);
      datosExcel.push([]);

      datosExcel.push(['RESUMEN DE ASISTENCIAS']);
      trimestres.forEach((trimestre) => {
        const asistencias = datosAlumno?.resumenAsistencias?.porTrimestre?.[trimestre]?.asistencias || 0;
        const inasistencias = datosAlumno?.resumenAsistencias?.porTrimestre?.[trimestre]?.inasistencias || 0;
        datosExcel.push([`${trimestre}° Trimestre:`, `Asistencias: ${asistencias}`, `Inasistencias: ${inasistencias}`]);
      });
      datosExcel.push([
        'TOTAL:',
        `Asistencias: ${datosAlumno?.resumenAsistencias?.total?.asistencias || 0}`,
        `Inasistencias: ${datosAlumno?.resumenAsistencias?.total?.inasistencias || 0}`
      ]);
      datosExcel.push([]);

      datosExcel.push(['OBSERVACIONES POR TRIMESTRE']);
      const observaciones = Object.keys(datosAlumno?.observacionesGenerales || {})
        .filter((trimestre) => datosAlumno?.observacionesGenerales?.[trimestre]?.trim())
        .map((trimestre) => `TRIMESTRE ${trimestre}: ${datosAlumno?.observacionesGenerales?.[trimestre]}`)
        .join('\n\n');
      if (observaciones) {
        datosExcel.push([observaciones]);
      }
      datosExcel.push([]);

      datosExcel.push(['SUGERENCIAS POR TRIMESTRE']);
      const sugerencias = Object.keys(datosAlumno?.sugerenciasGenerales || {})
        .filter((trimestre) => datosAlumno?.sugerenciasGenerales?.[trimestre]?.trim())
        .map((trimestre) => `TRIMESTRE ${trimestre}: ${datosAlumno?.sugerenciasGenerales?.[trimestre]}`)
        .join('\n\n');
      if (sugerencias) {
        datosExcel.push([sugerencias]);
      }

      const ws = XLSX.utils.aoa_to_sheet(datosExcel);
      ws['!cols'] = [
        { wch: 12 },
        ...todasLasMaterias.map(() => ({ wch: 12 })),
        { wch: 12 }
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Boleta de Calificaciones');

      const nombreArchivo = datosAlumno?.alumno?.nombre
        ? `boleta_${datosAlumno.alumno.nombre.replace(/\s+/g, '_')}.xlsx`
        : 'boleta_alumno.xlsx';

      XLSX.writeFile(wb, nombreArchivo);
    } catch (err) {
      console.error('❌ Error al generar Excel:', err);
      alert('Error al generar el archivo Excel: ' + err.message);
    }
  }, [datosAlumno, fase, categorias, trimestres]);

  const handleExportarPDF = useCallback(async () => {
    if (!datosAlumno) {
      return;
    }

    try {
      const boletaElement = document.getElementById('boleta');

      if (!boletaElement) {
        alert('Error: No se encontró la boleta para exportar');
        return;
      }

      const canvas = await html2canvas(boletaElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: -window.scrollY
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      let imgWidth = pageWidth - margin * 2;
      let imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (imgHeight > pageHeight - margin * 2) {
        imgHeight = pageHeight - margin * 2;
        imgWidth = (canvas.width * imgHeight) / canvas.height;
      }

      pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);

      const nombreArchivo = datosAlumno?.alumno?.nombre
        ? `boleta_${datosAlumno.alumno.nombre.replace(/\s+/g, '_')}.pdf`
        : 'boleta_alumno.pdf';

      pdf.save(nombreArchivo);
    } catch (err) {
      console.error('❌ Error al generar PDF:', err);
      alert('Error al generar el PDF: ' + err.message);
    }
  }, [datosAlumno]);

  return (
    <div className="container py-4">
      <h2 className="text-center mb-4">Consulta de Calificaciones</h2>

      <div className="row justify-content-center g-3 mb-4">
        <div className="col-sm-6 col-md-4">
          <label className="form-label">Grupo</label>
          <select
            className="form-select"
            value={grupoSeleccionado}
            onChange={handleGrupoChange}
            disabled={loadingGrupos || loading}
          >
            <option value="">Selecciona un grupo</option>
            {grupos.map((grupo) => (
              <option key={grupo.IdGrupo} value={grupo.IdGrupo}>
                {`${grupo.Grado ?? ''}° ${grupo.CicloEscolar ? `- ${grupo.CicloEscolar}` : ''}`.trim() || `Grupo ${grupo.IdGrupo}`}
              </option>
            ))}
          </select>
          {loadingGrupos && <small className="text-muted">Cargando grupos...</small>}
        </div>
        <div className="col-sm-6 col-md-4">
          <label className="form-label">Alumno</label>
          <select
            className="form-select"
            value={alumnoSeleccionado}
            onChange={handleAlumnoChange}
            disabled={!grupoSeleccionado || loadingAlumnos || loading}
          >
            <option value="">Selecciona un alumno</option>
            {alumnos.map((alumno) => (
              <option key={alumno.IdUsuario} value={alumno.IdUsuario}>
                {alumno.NombreCompleto}
              </option>
            ))}
          </select>
          {loadingAlumnos && <small className="text-muted">Cargando alumnos...</small>}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {loading && (
        <div className="text-center my-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3">Cargando boleta del alumno seleccionado...</p>
        </div>
      )}

      {!loading && alumnoSeleccionado && !datosAlumno && !error && (
        <div className="alert alert-info" role="alert">
          <i className="fas fa-info-circle me-2"></i>
          No se encontraron calificaciones para el alumno seleccionado.
        </div>
      )}

      {!loading && datosAlumno && (
        <div className="bg-light py-4">
          <div className="container">
            <div id="boleta" className="boleta" style={{ fontSize: '12px', maxWidth: '100%', overflow: 'hidden' }}>
              <div className="d-flex justify-content-between align-items-center mb-2 px-2">
                <img src={logoISC} alt="Logo ISC" style={{ height: '60px', objectFit: 'contain' }} />
                <div className="text-end">
                  <img src={logoSEP} alt="Logo SEP" style={{ height: '40px', objectFit: 'contain' }} />
                </div>
              </div>

              <div className="text-center fw-bold text-black mb-2 text-uppercase" style={{ fontSize: '18px' }}>Boleta de Evaluaciones Sección PRIMARIA</div>

              <div className="row mb-2" style={{ fontSize: '11px' }}>
                <div className="col-12 col-md-6"><strong>Nombre del alumno:</strong> <span id="nombreAlumno">{datosAlumno?.alumno?.nombre || 'Cargando...'}</span></div>
                <div className="col-6 col-md-3"><strong>Grupo:</strong> <span id="grupoAlumno">{datosAlumno?.alumno?.grupo || 'Cargando...'}</span></div>
                <div className="col-6 col-md-3 text-md-end"><strong>Fecha:</strong> {new Date().toLocaleDateString('es-ES')}</div>
              </div>

              <div className="row mb-2" style={{ fontSize: '11px' }}>
                <div className="col-6 col-md-3"><strong>Ciclo escolar:</strong> {datosAlumno?.alumno?.cicloEscolar || '2024-2025'}</div>
                <div className="col-6 col-md-3"><strong>C.C.T.:</strong> C.C.T. {CCT}</div>
                <div className="col-6 col-md-3"><strong>Fase:</strong> {fase ? `Fase ${fase}` : '—'}</div>
              </div>

              <table id="tablaBoleta" className="table table-bordered table-sm text-center" style={{ fontSize: '9px', marginBottom: '10px' }}>
                <thead>
                  <tr>
                    <th rowSpan="2">PERIODO TRIMESTRE</th>
                    {categorias.map((categoria, index) => {
                      const numMaterias = Object.keys(categoria.materias || {}).length;
                      if (numMaterias === 0) {
                        return null;
                      }

                      const colores = ['#F4D03F', '#85C1E9', '#A9DFBF', '#F8BBD9'];
                      const color = colores[index % colores.length];

                      return (
                        <th
                          key={categoria.nombre}
                          colSpan={numMaterias}
                          style={{ backgroundColor: color, color: '#000' }}
                        >
                          {categoria.nombre}
                        </th>
                      );
                    })}
                    <th rowSpan="2">PROMEDIO</th>
                  </tr>
                  <tr>
                    {categorias.map((categoria) =>
                      Object.keys(categoria.materias || {}).map((nombreMateria, materiaIndex) => (
                        <th key={`${categoria.nombre}-${materiaIndex}`} className="rotated">
                          <div>{nombreMateria.toUpperCase()}</div>
                        </th>
                      ))
                    )}
                  </tr>
                </thead>
                <tbody id="tablaAlumno">
                  {trimestres.map((trimestre) => (
                    <React.Fragment key={trimestre}>
                      <tr>
                        <td><strong>{trimestre}°</strong></td>
                        {categorias.map((categoria) =>
                          Object.entries(categoria.materias || {}).map(([nombreMateria, materiaData], materiaIndex) => (
                            <td key={`${trimestre}-${categoria.nombre}-${materiaIndex}`}>
                              {materiaData.calificaciones?.[trimestre]?.calificacion || '-'}
                            </td>
                          ))
                        )}
                        <td><strong>{datosAlumno.promedios?.porTrimestre?.[trimestre] || '-'}</strong></td>
                      </tr>
                      <tr className="table-primary">
                        <td className="text-end text-primary fw-bold" style={{ fontSize: '0.7rem' }}>
                          Prom. {trimestre}°
                        </td>
                        {categorias.map((categoria, catIndex) => {
                          const numMaterias = Object.keys(categoria.materias || {}).length;
                          if (numMaterias === 0) {
                            return null;
                          }

                          const promedio = promediosCategoriaPorTrimestre[trimestre]?.[catIndex] ?? null;
                          return (
                            <td
                              key={`prom-${trimestre}-${categoria.nombre}`}
                              colSpan={numMaterias}
                              className="text-center text-primary fw-bold"
                              style={{ fontSize: '0.75rem' }}
                            >
                              {promedio !== null ? promedio.toFixed(2) : '-'}
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>
                    </React.Fragment>
                  ))}
                  <tr className="table-info">
                    <td><strong>PROM. CATEGORÍA</strong></td>
                    {categorias.map((categoria, catIndex) => {
                      const numMaterias = Object.keys(categoria.materias || {}).length;
                      if (numMaterias === 0) {
                        return null;
                      }

                      return (
                        <td
                          key={`prom-general-${categoria.nombre}`}
                          colSpan={numMaterias}
                          className="text-center"
                        >
                          <strong>
                            {promediosCategorias[catIndex] !== null && promediosCategorias[catIndex] !== undefined
                              ? promediosCategorias[catIndex].toFixed(2)
                              : '-'}
                          </strong>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>
                  <tr className="table-warning">
                    <td><strong>PROMEDIO FINAL</strong></td>
                    <td colSpan={Math.max(totalColumnasMaterias, 1)} className="text-center">
                      <strong>{datosAlumno.promedios?.general || '0.0'}</strong>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className="attendance">
                    <td>Asistencias</td>
                    {categorias.map((categoria, catIndex) => {
                      const numMaterias = Object.keys(categoria.materias || {}).length;
                      const trimestre = catIndex + 1;
                      return numMaterias > 0 ? (
                        <td key={catIndex} colSpan={numMaterias}>
                          <strong>{trimestre}°</strong> {datosAlumno.resumenAsistencias?.porTrimestre?.[trimestre]?.asistencias || 0}
                        </td>
                      ) : null;
                    })}
                    <td>TOTAL {datosAlumno.resumenAsistencias?.total?.asistencias || 0}</td>
                  </tr>
                  <tr className="attendance">
                    <td>Inasistencias</td>
                    {categorias.map((categoria, catIndex) => {
                      const numMaterias = Object.keys(categoria.materias || {}).length;
                      const trimestre = catIndex + 1;
                      return numMaterias > 0 ? (
                        <td key={catIndex} colSpan={numMaterias}>
                          <strong>{trimestre}°</strong> {datosAlumno.resumenAsistencias?.porTrimestre?.[trimestre]?.inasistencias || 0}
                        </td>
                      ) : null;
                    })}
                    <td>TOTAL {datosAlumno.resumenAsistencias?.total?.inasistencias || 0}</td>
                  </tr>
                </tfoot>
              </table>

              <div className="mt-2">
                <h6 style={{ fontSize: '12px', marginBottom: '5px' }}>Observaciones por Trimestre</h6>
                <textarea
                  rows="4"
                  id="obsAlumno"
                  className="form-control"
                  style={{ fontSize: '10px' }}
                  readOnly
                  value={Object.keys(datosAlumno?.observacionesGenerales || {})
                    .filter((trimestre) => datosAlumno?.observacionesGenerales?.[trimestre]?.trim())
                    .map((trimestre) => `TRIMESTRE ${trimestre}:\n${datosAlumno?.observacionesGenerales?.[trimestre]}`)
                    .join('\n\n')}
                ></textarea>
              </div>

              <div className="mt-2">
                <h6 style={{ fontSize: '12px', marginBottom: '5px' }}>Sugerencias por Trimestre</h6>
                <textarea
                  rows="4"
                  id="sugAlumno"
                  className="form-control"
                  style={{ fontSize: '10px' }}
                  readOnly
                  value={Object.keys(datosAlumno?.sugerenciasGenerales || {})
                    .filter((trimestre) => datosAlumno?.sugerenciasGenerales?.[trimestre]?.trim())
                    .map((trimestre) => `TRIMESTRE ${trimestre}:\n${datosAlumno?.sugerenciasGenerales?.[trimestre]}`)
                    .join('\n\n')}
                ></textarea>
              </div>

              <div className="mt-4">
                <h5 className="text-center mb-3">Detalle de Calificaciones por Categoría</h5>
                {categorias
                  .filter((categoria) => Object.keys(categoria.materias || {}).length > 0)
                  .map((categoria, catIndex) => (
                    <div key={catIndex} className="mb-4">
                      <h6 className="bg-primary text-white p-2 mb-3">
                        {categoria.nombre}
                        <span className="badge bg-light text-primary ms-2">
                          {Object.keys(categoria.materias).length} materia(s)
                        </span>
                      </h6>
                      <div className="table-responsive">
                        <table className="table table-bordered table-sm">
                          <thead className="table-light">
                            <tr>
                              <th rowSpan="2">Materia</th>
                              <th rowSpan="2">Horario</th>
                              <th colSpan="3" className="text-center">Calificaciones por Trimestre</th>
                              <th rowSpan="2">Docente(s)</th>
                            </tr>
                            <tr>
                              <th className="text-center">1°</th>
                              <th className="text-center">2°</th>
                              <th className="text-center">3°</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.values(categoria.materias || {}).map((materia, matIndex) => (
                              <tr key={matIndex}>
                                <td><strong>{materia.nombre}</strong></td>
                                <td>
                                  {materia.horario && materia.horario.length > 0 ? (
                                    <div>
                                      {materia.horario.map((horario, hIndex) => (
                                        <div key={hIndex} className="mb-1">
                                          <small>
                                            <strong>{horario.dia}</strong><br />
                                            {horario.horaInicio?.slice(0, 5)} - {horario.horaFin?.slice(0, 5)}<br />
                                            <span className="text-muted">Aula: {horario.aula}</span>
                                          </small>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <small className="text-muted">Sin horario</small>
                                  )}
                                </td>
                                {[1, 2, 3].map((trimestre) => (
                                  <td key={trimestre} className="text-center">
                                    {materia.calificaciones?.[trimestre] ? (
                                      <div>
                                        <span className="badge bg-success fs-6">
                                          {materia.calificaciones?.[trimestre]?.calificacion}
                                        </span>
                                        <br />
                                        <small className="text-muted">
                                          A: {materia.calificaciones?.[trimestre]?.asistencias || 0} |
                                          I: {materia.calificaciones?.[trimestre]?.inasistencias || 0}
                                        </small>
                                        {materia.calificaciones?.[trimestre]?.observaciones && (
                                          <div>
                                            <small className="text-info d-block mt-1">
                                              📝 {materia.calificaciones?.[trimestre]?.observaciones}
                                            </small>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div>
                                        <span className="text-muted">Pendiente</span>
                                        <br />
                                        <small className="text-muted">No calificado</small>
                                      </div>
                                    )}
                                  </td>
                                ))}
                                <td>
                                  {materia.docentes && materia.docentes.length > 0 ? (
                                    <div>
                                      {materia.docentes.map((docente, dIndex) => (
                                        <small key={dIndex} className="d-block">
                                          👨‍🏫 {docente}
                                        </small>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-muted">Sin asignar</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
              </div>

              <div className="text-center mt-2">
                <img src={firmaCoordinadora} alt="Firma Coordinadora" style={{ maxHeight: '50px' }} /><br />
                <strong style={{ fontSize: '11px' }}>Mtra. Ana María Ramos Cortés</strong><br />
                <span style={{ fontSize: '10px' }}>Coordinadora de Primaria</span>
              </div>

              <div className="d-flex justify-content-center gap-3 mt-4">
                <button
                  className="btn btn-danger"
                  onClick={handleExportarPDF}
                  disabled={!datosAlumno}
                >
                  Exportar PDF
                </button>
                <button
                  className="btn btn-warning"
                  onClick={handleExportarExcel}
                  disabled={!datosAlumno}
                >
                  Exportar Excel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default ConsultaCalificaciones;
